import { motion } from "framer-motion";
import React, { useState, useEffect } from "react";

function MainHeader({ onBackToDashboard }: { onBackToDashboard: () => void }) {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [appMode, setAppMode] = useState<string>("crypto");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  // App mode options
  const appModes = [
    { value: "stock", label: "Stock" },
    { value: "crypto", label: "Crypto" },
  ];

  // Get current user and app mode from localStorage
  useEffect(() => {
    const userData = localStorage.getItem("adminUser");
    if (userData) {
      try {
        setCurrentUser(JSON.parse(userData));
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }

    // Get app mode from localStorage
    const savedAppMode = localStorage.getItem("appMode");
    if (savedAppMode) {
      setAppMode(savedAppMode);
    } else {
      setAppMode("crypto");
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    localStorage.removeItem("tokenExpiry");
    window.location.reload();
  };

  const handleAppModeChange = (mode: string) => {
    setAppMode(mode);
    localStorage.setItem("appMode", mode);
    setIsDropdownOpen(false);
    window.location.reload();
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest(".dropdown-container")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div>
      <motion.div
        className="bg-white shadow-sm border-b border-gray-200"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <motion.div
              className="flex items-center space-x-4"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="flex-shrink-0">
                <motion.div
                  className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </motion.div>
              </div>
              <h1 className="text-xl font-bold text-gray-900">
                TPL Admin Panel
              </h1>
            </motion.div>
            <motion.div
              className="flex items-center space-x-4"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <span className="text-sm text-gray-600">
                Welcome, {currentUser?.email || "Admin"}
              </span>

              {/* App Mode Dropdown */}
              <div className="relative dropdown-container">
                <motion.button
                  onClick={toggleDropdown}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 border shadow-md ${
                    isDropdownOpen
                      ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-grey border-indigo-400 shadow-indigo-200"
                      : "bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-indigo-200 hover:from-indigo-100 hover:to-purple-100 hover:border-indigo-300 hover:shadow-indigo-100"
                  }`}
                  whileHover={{
                    scale: 1.02,
                    boxShadow: "0 4px 12px rgba(99, 102, 241, 0.2)",
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div
                    className={`p-1 rounded-md ${
                      isDropdownOpen ? "bg-white/20" : "bg-indigo-100"
                    }`}
                    // animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {appMode === "stock" ? (
                      <svg
                        className={`w-3.5 h-3.5 ${
                          isDropdownOpen ? "text-white" : "text-indigo-600"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className={`w-3.5 h-3.5 ${
                          isDropdownOpen ? "text-white" : "text-indigo-600"
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                        />
                      </svg>
                    )}
                  </motion.div>
                  <span className="font-semibold">
                    Mode:{" "}
                    {appModes.find((mode) => mode.value === appMode)?.label}
                  </span>
                  <motion.svg
                    className={`w-4 h-4 transition-all duration-300 ${
                      isDropdownOpen
                        ? "rotate-180 text-white"
                        : "text-indigo-500"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </motion.svg>
                </motion.button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <motion.div
                    className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-indigo-100 z-50 overflow-hidden"
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <div className="py-1">
                      {appModes.map((mode, index) => (
                        <motion.button
                          key={mode.value}
                          onClick={() => handleAppModeChange(mode.value)}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-all duration-200 hover:bg-indigo-50 ${
                            appMode === mode.value
                              ? "bg-indigo-100 text-indigo-800 font-semibold"
                              : "text-gray-700 hover:text-indigo-700"
                          }`}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.15, delay: index * 0.05 }}
                          whileHover={{ x: 2 }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <motion.div
                                className={`p-1.5 rounded-md ${
                                  appMode === mode.value
                                    ? "bg-indigo-200"
                                    : "bg-gray-100"
                                }`}
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.2 }}
                              >
                                {mode.value === "stock" ? (
                                  <svg
                                    className={`w-3.5 h-3.5 ${
                                      appMode === mode.value
                                        ? "text-indigo-600"
                                        : "text-gray-500"
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className={`w-3.5 h-3.5 ${
                                      appMode === mode.value
                                        ? "text-indigo-600"
                                        : "text-gray-500"
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                                    />
                                  </svg>
                                )}
                              </motion.div>
                              <span className="font-medium">{mode.label}</span>
                            </div>
                            {appMode === mode.value && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.2 }}
                              >
                                <svg
                                  className="w-4 h-4 text-indigo-600"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </motion.div>
                            )}
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              <motion.button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors duration-200"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Logout
              </motion.button>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default MainHeader;
